import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import type { z } from 'zod';

/**
 * Mints a UUID-shaped opaque key for `Idempotency-Key`.
 *
 * The server's `IdempotencyService` namespaces the cache by user +
 * scope, so collisions only need to be unique within a single user's
 * mutation attempts. `Math.random()` + a timestamp is sufficient
 * entropy without pulling `react-native-get-random-values`.
 */
export function mintIdempotencyKey(): string {
  const ts = Date.now().toString(36);
  const r1 = Math.random().toString(36).slice(2, 12);
  const r2 = Math.random().toString(36).slice(2, 12);
  return `${ts}-${r1}-${r2}`;
}

/**
 * Inject an `Idempotency-Key` header into an axios request config so
 * mutations are de-duplicated by the server's `IdempotencyService`.
 * Always pair with a fresh key per mutation attempt — the server caches
 * the *response* under the key for 24h, so re-using a key would return
 * a stale create result.
 */
export function withIdempotencyKey(
  key: string,
  config: AxiosRequestConfig = {},
): AxiosRequestConfig {
  return {
    ...config,
    headers: {
      ...(config.headers ?? {}),
      'Idempotency-Key': key,
    },
  };
}

/**
 * GET + parse helper. Throws on non-2xx (axios already does); the
 * response interceptor in `apps/mobile/lib/api/createAxios.ts` has
 * already mapped errors to `AppError`. Zod parses run at the
 * boundary so screens never see drift between client expectations
 * and server reality.
 *
 * The schema's *output* is what the hook returns — using `z.output<S>`
 * lets us keep the helper signatures generic without sacrificing
 * inference at every call site.
 */
export async function getJSON<S extends z.ZodTypeAny>(
  http: AxiosInstance,
  url: string,
  schema: S,
  config?: AxiosRequestConfig,
): Promise<z.output<S>> {
  const response = await http.get(url, config);
  return schema.parse(response.data) as z.output<S>;
}

/**
 * POST + parse helper. Always supplies a fresh `Idempotency-Key` so
 * caller code only worries about the parsed payload, not the
 * de-duplication contract.
 */
export async function postJSON<S extends z.ZodTypeAny>(
  http: AxiosInstance,
  url: string,
  body: unknown,
  schema: S,
  config?: AxiosRequestConfig,
): Promise<z.output<S>> {
  const response = await http.post(url, body, withIdempotencyKey(mintIdempotencyKey(), config));
  return schema.parse(response.data) as z.output<S>;
}

export async function patchJSON<S extends z.ZodTypeAny>(
  http: AxiosInstance,
  url: string,
  body: unknown,
  schema: S,
  config?: AxiosRequestConfig,
): Promise<z.output<S>> {
  const response = await http.patch(url, body, config);
  return schema.parse(response.data) as z.output<S>;
}

export async function deleteJSON<S extends z.ZodTypeAny>(
  http: AxiosInstance,
  url: string,
  schema: S,
  config?: AxiosRequestConfig,
): Promise<z.output<S>> {
  const response = await http.delete(url, config);
  return schema.parse(response.data) as z.output<S>;
}
